import {
  Button,
  IconHeart,
  IconInfo,
  IconMail,
  IconMapPin,
  MobileTabBar,
  TopBar,
} from '@fontbold/ui';
import { useNavigate } from '@tanstack/react-router';
import { useCookieConsent } from '#/lib/consent';
import type { Space } from '#/lib/queries/space';
import { useSaved } from '#/lib/saved';
import { showsReels } from '#/lib/site';
import { DEFAULT_LOGO, spaceLogo } from '#/lib/space-logo';
import {
  NEWSLETTER_BUTTON_LABEL,
  TOP_BAR_TRANSLATIONS,
} from '#/lib/translations';

export type ChromeKey = 'home' | 'kaart' | 'bewaard' | 'over';

const LINKS: { key: ChromeKey; label: string; href: string }[] = [
  { key: 'home', label: 'Reels', href: '/' },
  { key: 'kaart', label: 'Kaart', href: '/kaart' },
  { key: 'bewaard', label: 'Bewaard', href: '/bewaard' },
  { key: 'over', label: 'Over', href: '/over' },
];

const brandLogo = spaceLogo;

function showsLink(space: Space, key: ChromeKey, functional: boolean): boolean {
  if (key === 'home') return showsReels(space);
  if (key === 'bewaard') return space.features.saved && functional;
  return true;
}

export function AppTopBar({
  space,
  active,
  onNewsletter,
  searchValue,
}: {
  space: Space;
  active: ChromeKey;
  onNewsletter: () => void;
  /** Current query when the page owns a search (the map). */
  searchValue?: string;
}) {
  const { count } = useSaved();
  const { functional } = useCookieConsent();
  const navigate = useNavigate();

  return (
    <TopBar
      brandName={space.name}
      brandLogo={brandLogo(space)}
      translations={TOP_BAR_TRANSLATIONS}
      searchValue={searchValue}
      onSearch={(query) =>
        navigate({
          to: '/kaart',
          search: query ? { q: query } : {},
        })
      }
      actions={
        <Button variant="primary" size="md" onClick={onNewsletter}>
          <IconMail size={16} color="var(--content-inverted)" />
          {NEWSLETTER_BUTTON_LABEL}
        </Button>
      }
      links={LINKS.filter((link) => showsLink(space, link.key, functional)).map(
        (link) => ({
          label: link.label,
          href: link.href,
          active: link.key === active,
          badge: link.key === 'bewaard' && count > 0 ? count : undefined,
        }),
      )}
    />
  );
}

export function AppTabBar({
  space,
  active,
}: {
  space: Space;
  active: ChromeKey;
}) {
  const logo = brandLogo(space);
  const { functional } = useCookieConsent();

  const tabs = [
    {
      key: 'home' as const,
      label: 'Home',
      href: '/',
      active: active === 'home',
      icon: (
        <img
          src={logo}
          alt=""
          onError={(event) => {
            event.currentTarget.src = DEFAULT_LOGO;
          }}
          style={{
            width: 22,
            height: 22,
            borderRadius: 999,
            objectFit: 'cover',
            border: `2px solid ${
              active === 'home' ? 'var(--accent-500)' : 'var(--border-strong)'
            }`,
          }}
        />
      ),
    },
    {
      key: 'kaart' as const,
      label: 'Kaart',
      href: '/kaart',
      active: active === 'kaart',
      icon: <IconMapPin size={23} />,
    },
    {
      key: 'bewaard' as const,
      label: 'Bewaard',
      href: '/bewaard',
      active: active === 'bewaard',
      icon: <IconHeart size={23} />,
    },
    {
      key: 'over' as const,
      label: 'Over',
      href: '/over',
      active: active === 'over',
      icon: <IconInfo size={23} />,
    },
  ];

  return (
    <MobileTabBar
      tabs={tabs.filter(
        (tab) => tab.key === 'home' || showsLink(space, tab.key, functional),
      )}
    />
  );
}
